import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useOrg } from "../lib/session";
import { useEntitlements } from "../lib/plans";
import { ago } from "../lib/format";
import type { Agent } from "../lib/types";
import { Widgets, statusLabel } from "./agents";
import { useEmail, providerName } from "../lib/email";
import { useIntegrations } from "../lib/integrations";
import { ErrorText, Field, Form, Loading, PageHeader, str } from "../components/ui";

interface Connection {
  id: string;
  channel: string;
  status: "active" | "disabled";
  agent_id: string | null;
  agent_name: string | null;
  phone_number_id: string;
  display_name: string | null;
  display_phone: string | null;
  last_error: string | null;
  last_error_at: string | null;
  created_at: string;
}

interface SignupSettings {
  app_id: string;
  config_id: string;
  graph_version: string;
}

// Minimal typing for the parts of the Facebook JS SDK we use.
interface FacebookSDK {
  init(opts: { appId: string; autoLogAppEvents: boolean; xfbml: boolean; version: string }): void;
  login(cb: (r: { authResponse?: { code?: string } | null; status?: string }) => void, opts: Record<string, unknown>): void;
}
declare global {
  interface Window {
    FB?: FacebookSDK;
    fbAsyncInit?: () => void;
  }
}

let sdkPromise: Promise<FacebookSDK> | null = null;

// loadFacebook loads Meta's SDK once and initialises it with the SVENEX app.
function loadFacebook(s: SignupSettings): Promise<FacebookSDK> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<FacebookSDK>((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId: s.app_id, autoLogAppEvents: true, xfbml: false, version: s.graph_version });
      resolve(window.FB!);
    };
    const tag = document.createElement("script");
    tag.src = "https://connect.facebook.net/en_US/sdk.js";
    tag.async = true;
    tag.defer = true;
    tag.crossOrigin = "anonymous";
    tag.onerror = () => {
      sdkPromise = null;
      reject(new ApiError(0, "sdk_failed", "Couldn't load Facebook. Check that ad blockers or privacy extensions aren't blocking connect.facebook.net, then try again."));
    };
    document.body.appendChild(tag);
  });
  return sdkPromise;
}

interface SignupResult {
  phone_number_id: string;
  waba_id: string;
  business_id?: string;
}

// ConnectWhatsApp runs Meta's Embedded Signup: the customer logs in with
// Facebook, picks or creates their business and number in Meta's popup, and
// we receive a short-lived code plus the IDs. The server does the rest.
function ConnectWhatsApp({ settings, agents, onConnected }: { settings: SignupSettings; agents: { id: string; name: string }[]; onConnected: () => void }) {
  const { base: orgBase } = useOrg();
  const baseRef = useRef(orgBase);
  baseRef.current = orgBase;
  const base = () => baseRef.current;
  const [agentId, setAgentId] = useState(agents[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const pending = useRef<{ code?: string; ids?: SignupResult; done: boolean }>({ done: true });
  const agentRef = useRef(agentId);
  agentRef.current = agentId;

  useEffect(() => {
    if (!agentId && agents[0]) setAgentId(agents[0].id);
  }, [agents, agentId]);

  useEffect(() => {
    loadFacebook(settings).catch(() => {}); // warm up so the popup opens instantly
    const onMessage = (e: MessageEvent) => {
      let host = "";
      try {
        host = new URL(e.origin).hostname;
      } catch {
        return;
      }
      if (host !== "facebook.com" && !host.endsWith(".facebook.com")) return;
      let msg: { type?: string; event?: string; data?: Record<string, string> };
      try {
        msg = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (msg?.type !== "WA_EMBEDDED_SIGNUP" || pending.current.done) return;
      if (msg.event === "FINISH" && msg.data?.phone_number_id && msg.data?.waba_id) {
        pending.current.ids = { phone_number_id: msg.data.phone_number_id, waba_id: msg.data.waba_id, business_id: msg.data.business_id };
        void finish();
      } else if (msg.event === "FINISH_ONLY_WABA") {
        stop(null, "Your WhatsApp Business account was created, but no phone number was added. Click Connect WhatsApp again and add a number.");
      } else if (msg.event === "CANCEL") {
        stop(null, msg.data?.error_message ? `Meta reported: ${msg.data.error_message}` : "Setup was closed before it finished. You can start again any time.");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  function stop(err: unknown, note: string | null = null) {
    pending.current.done = true;
    setBusy(false);
    setError(err);
    setNotice(note);
  }

  // finish sends the code and IDs to the server once both have arrived (the
  // popup's message and the login callback come in either order).
  async function finish() {
    const p = pending.current;
    if (p.done || !p.code || !p.ids) return;
    p.done = true;
    try {
      await api(`${base()}/channels/whatsapp/embedded`, {
        method: "POST",
        json: {
          code: p.code,
          phone_number_id: p.ids.phone_number_id,
          business_account_id: p.ids.waba_id,
          ...(p.ids.business_id && { business_id: p.ids.business_id }),
          ...(agentRef.current && { agent_id: agentRef.current }),
        },
      });
      setBusy(false);
      setNotice(null);
      onConnected();
    } catch (err) {
      stop(err);
    }
  }

  async function start() {
    setError(null);
    setNotice(null);
    setBusy(true);
    let FB: FacebookSDK;
    try {
      FB = await loadFacebook(settings);
    } catch (err) {
      stop(err);
      return;
    }
    pending.current = { done: false };
    // The callback must not be async: the SDK rejects promise-returning callbacks.
    FB.login(
      (resp) => {
        const code = resp.authResponse?.code;
        if (!code) {
          if (!pending.current.done) stop(null, "Facebook login was cancelled. You can start again any time.");
          return;
        }
        pending.current.code = code;
        void finish();
        // If Meta never sends the number details, don't spin forever.
        setTimeout(() => {
          if (!pending.current.done && !pending.current.ids) stop(null, "Meta didn't send the WhatsApp number details. Please try again.");
        }, 15000);
      },
      {
        config_id: settings.config_id,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
      },
    );
  }

  return (
    <div className="whatsapp-signup">
      <Field label="Agent that answers">
        <select value={agentId} onChange={(e) => setAgentId(e.target.value)} disabled={busy}>
          <option value="">No agent (staff only)</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <div>
        <button className="whatsapp-connect" onClick={start} disabled={busy}>{busy ? "Connecting…" : "Connect WhatsApp"}</button>
      </div>
      {notice && <p className="muted small">{notice}</p>}
      {error != null && <ErrorText error={error} />}
    </div>
  );
}

export default function Channels() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const plan = useEntitlements();
  const key = [orgId, "channels"];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ connections: Connection[]; whatsapp_available: boolean; embedded_signup?: SignupSettings }>(`${base}/channels`) });
  const agents = useQuery({ queryKey: [orgId, "agents"], queryFn: () => api<{ agents: Agent[] }>(`${base}/agents`) });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const integ = useIntegrations();
  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorText error={list.error} />;
  const { connections, whatsapp_available, embedded_signup } = list.data!;
  const max = plan.limit("whatsapp_numbers");
  const canAdd = plan.enabled("whatsapp_channel") && whatsapp_available && (max === undefined || connections.length < max);
  return (
    <>
      <PageHeader title="Channels" />
      <p className="muted">Everywhere customers can reach you, and which AI agent answers there. Every conversation lands in the Inbox, where your team can take over.</p>

      {integ.has("web_chat") && (
        <>
          <h2 className="section-title">Website chat</h2>
          <WebChat agents={agents.data?.agents} loading={agents.isLoading} />
        </>
      )}

      {(integ.has("whatsapp") || connections.length > 0) && <h2 className="section-title">WhatsApp</h2>}

      {connections.map((c) => (
        <section key={c.id} className="card">
          <div className="inline" style={{ justifyContent: "space-between" }}>
            <div>
              <b>WhatsApp · {c.display_phone ?? c.phone_number_id}</b> {c.display_name && <span className="muted">({c.display_name})</span>}
              <div className="muted small">Connected {ago(c.created_at)} · {c.status === "active" ? "receiving messages" : "turned off"} · <Link to={`/o/${orgId}/channels/${c.id}/templates`}>Message templates</Link></div>
            </div>
            <div className="actions">
              <select aria-label="Agent" value={c.agent_id ?? ""} onChange={async (e) => {
                await api(`${base}/channels/${c.id}`, { method: "PATCH", json: e.target.value ? { agent_id: e.target.value } : { clear_agent: true } });
                refresh();
              }}>
                <option value="">No agent (staff only)</option>
                {agents.data?.agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button className="secondary" onClick={async () => {
                await api(`${base}/channels/${c.id}`, { method: "PATCH", json: { status: c.status === "active" ? "disabled" : "active" } });
                refresh();
              }}>{c.status === "active" ? "Turn off" : "Turn on"}</button>
              <button className="secondary" onClick={async () => {
                if (!confirm("Disconnect this number? Past conversations stay in the inbox.")) return;
                await api(`${base}/channels/${c.id}`, { method: "DELETE" });
                refresh();
              }}>Disconnect</button>
            </div>
          </div>
          {c.last_error && (
            <div className="notice small">
              <p className="error">{c.last_error}</p>
              <Form submitLabel="Update token" onSubmit={async (d) => {
                await api(`${base}/channels/${c.id}`, { method: "PATCH", json: { access_token: str(d, "token") } });
                refresh();
              }}>
                <Field label="New permanent access token"><input name="token" type="password" required autoComplete="off" /></Field>
              </Form>
            </div>
          )}
        </section>
      ))}

      {!integ.has("whatsapp") ? null : !plan.enabled("whatsapp_channel") ? (
        <section className="card"><p className="muted">WhatsApp isn't included in your plan. Contact SVENEX to add it.</p></section>
      ) : !whatsapp_available ? (
        <section className="card"><p className="muted">WhatsApp isn't available on this server yet.</p></section>
      ) : canAdd ? (
        <section className="card">
          <h2>Connect a WhatsApp number</h2>
          {embedded_signup && (
            <>
              <p className="muted small">
                Log in with Facebook, choose or create your business, and add the number you want customers to message.
                It takes about two minutes. Afterwards, add a payment method in WhatsApp Manager so Meta can bill conversations.
              </p>
              <ConnectWhatsApp settings={embedded_signup} agents={agents.data?.agents ?? []} onConnected={refresh} />
            </>
          )}
          <ManualConnect collapsed={!!embedded_signup}>
          <p className="muted small">
            In Meta Business Suite → WhatsApp Manager / the WhatsApp app dashboard, copy the <b>Phone number ID</b> and <b>WhatsApp Business Account ID</b>,
            and create a <b>permanent access token</b> for a system user with the whatsapp_business_messaging and whatsapp_business_management permissions.
            We check the details with Meta and store the token encrypted.
          </p>
          <Form submitLabel="Connect" onSubmit={async (d) => {
            await api(`${base}/channels/whatsapp`, { method: "POST", json: {
              phone_number_id: str(d, "phone"), business_account_id: str(d, "waba"), access_token: str(d, "token"), ...(str(d, "agent") && { agent_id: str(d, "agent") }),
            } });
            refresh();
          }}>
            <div className="row">
              <Field label="Phone number ID"><input name="phone" required inputMode="numeric" autoComplete="off" /></Field>
              <Field label="WhatsApp Business Account ID"><input name="waba" required inputMode="numeric" autoComplete="off" /></Field>
            </div>
            <Field label="Permanent access token"><input name="token" type="password" required autoComplete="off" /></Field>
            <Field label="Agent that answers">
              <select name="agent" defaultValue={agents.data?.agents[0]?.id ?? ""}>
                <option value="">No agent (staff only)</option>
                {agents.data?.agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
          </Form>
          </ManualConnect>
        </section>
      ) : (
        <p className="muted small">Your plan allows {max} WhatsApp number{max === 1 ? "" : "s"}.</p>
      )}

      {(integ.has("gmail") || integ.has("outlook_mail")) && (
        <>
          <h2 className="section-title">Email</h2>
          <EmailChannel />
        </>
      )}
      {integ.data && !integ.data.all && <p className="muted small">Something missing? SVENEX sets up the platforms your company uses. <a href="mailto:feras@svenex.app">Ask for another one</a>.</p>}
    </>
  );
}

/** WebChat: each agent's chat link and website code. */
function WebChat({ agents, loading }: { agents?: Agent[]; loading: boolean }) {
  const { orgId } = useOrg();
  const [open, setOpen] = useState<string | null>(null);
  if (loading) return <Loading />;
  if (!agents || agents.length === 0) {
    return (
      <section className="card inline-card">
        <div><b>No AI agent yet</b><p className="muted small">Make an agent first. Then its chat link and website code appear here.</p></div>
        <Link className="button-link" to={`/o/${orgId}/agents/new`}>New agent</Link>
      </section>
    );
  }
  return (
    <>
      {agents.map((a) => (
        <section key={a.id} className="card">
          <div className="inline-card">
            <div>
              <b>{a.name}</b> <span className={`status ${a.status}`}>{statusLabel[a.status] ?? a.status}</span>
              <p className="muted small">The chat link and the one line of code for your website.</p>
            </div>
            <button className="secondary" aria-expanded={open === a.id} onClick={() => setOpen(open === a.id ? null : a.id)}>{open === a.id ? "Hide" : "Link and code"}</button>
          </div>
          {open === a.id && <AgentWidgets id={a.id} />}
        </section>
      ))}
    </>
  );
}

function AgentWidgets({ id }: { id: string }) {
  const { base, orgId } = useOrg();
  const agent = useQuery({ queryKey: [orgId, "agent", id], queryFn: () => api<Agent>(`${base}/agents/${id}`) });
  if (agent.isLoading) return <Loading />;
  if (agent.error) return <ErrorText error={agent.error} />;
  return <Widgets agent={agent.data!} bare />;
}

/** EmailChannel: the connected mailboxes, managed on the Email page. */
function EmailChannel() {
  const { orgId, allows } = useOrg();
  const plan = useEntitlements();
  const email = useEmail();
  if (!allows("email") || !plan.enabled("email_channel")) {
    return <section className="card"><p className="muted small">Email isn't included in your plan. Contact SVENEX to add it.</p></section>;
  }
  if (email.isLoading) return <Loading />;
  const accounts = email.data?.accounts ?? [];
  return (
    <section className="card">
      <div className="inline-card">
        <div>
          <b>{accounts.length === 0 ? "No mailbox connected" : `${accounts.length} mailbox${accounts.length === 1 ? "" : "es"} connected`}</b>
          <p className="muted small">Connect Gmail or Outlook so customer emails land in the Inbox and the AI can draft or send replies.</p>
        </div>
        <Link className="button-link secondary" to={`/o/${orgId}/settings/email`}>{accounts.length === 0 ? "Connect a mailbox" : "Manage email"}</Link>
      </div>
      {accounts.length > 0 && (
        <ul className="plain-list">
          {accounts.map((m) => (
            <li key={m.id} className="inline small">
              <span>{providerName[m.provider]} · {m.email_address}</span>
              <span className="muted">{m.kind === "shared" ? "shared" : `personal${m.owner_name ? `, ${m.owner_name}` : ""}`}</span>
              {m.status !== "active" && <span className="tag warn-tag">{m.status === "needs_reconnect" ? "Needs reconnecting" : "Off"}</span>}
            </li>
          ))}
        </ul>
      )}
      <ErrorText error={email.error} />
    </section>
  );
}

function ManualConnect({ collapsed, children }: { collapsed: boolean; children: ReactNode }) {
  if (!collapsed) return <>{children}</>;
  return (
    <details className="manual-connect">
      <summary className="small">Connect manually with an access token (advanced)</summary>
      {children}
    </details>
  );
}
